import { Draft, Entry, draftKey, legacyToEntry, sameDay } from './entry';
import { newSeed } from '../chaos/rng';

// The only reader/writer of the Mania library. Raw IndexedDB, no dependency.
// DB `mania` v1: store `entries` (keyPath id, index `date`), store `draft` (out-of-line key).
const DB_NAME = 'mania';
const DB_VERSION = 1;
const LEGACY_DRAFT_KEY = 'current'; // pre-v2 single draft slot; migrated to per-target keys
const LEGACY_KEY = 'local_journals';
const MIGRATED_FLAG = 'mania_migrated_v1';

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('IndexedDB unavailable')); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('entries')) db.createObjectStore('entries', { keyPath: 'id' }).createIndex('date', 'date');
      if (!db.objectStoreNames.contains('draft')) db.createObjectStore('draft');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    req.onblocked = () => reject(new Error('IndexedDB blocked'));
  });
  dbPromise.catch(() => { dbPromise = null; });
  return dbPromise;
}

function run<T>(store: 'entries' | 'draft', mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T> {
  return open().then((db) => new Promise<T>((resolve, reject) => {
    const tx = db.transaction(store, mode);
    const req = fn(tx.objectStore(store));
    let result: T;
    if (req) req.onsuccess = () => { result = req.result; };
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
  }));
}

// One-time move of the legacy single 'current' draft to its per-target key. Never
// overwrites an existing keyed draft; the legacy slot is deleted either way.
let legacyDraftMigrated: Promise<void> | null = null;
function migrateLegacyDraft(): Promise<void> {
  if (legacyDraftMigrated) return legacyDraftMigrated;
  legacyDraftMigrated = open().then((db) => new Promise<void>((resolve, reject) => {
    const tx = db.transaction('draft', 'readwrite');
    const store = tx.objectStore('draft');
    const getLegacy = store.get(LEGACY_DRAFT_KEY);
    getLegacy.onsuccess = () => {
      const legacy = getLegacy.result as Draft | undefined;
      if (!legacy) return;
      const key = draftKey(legacy.editingId ?? null);
      const getKeyed = store.get(key);
      getKeyed.onsuccess = () => {
        if (getKeyed.result === undefined) store.put(legacy, key);
        store.delete(LEGACY_DRAFT_KEY);
      };
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('draft migration failed'));
    tx.onabort = () => reject(tx.error ?? new Error('draft migration aborted'));
  }));
  legacyDraftMigrated.catch(() => { legacyDraftMigrated = null; });
  return legacyDraftMigrated;
}

export const libraryDb = {
  async isAvailable(): Promise<boolean> {
    try { await open(); return true; } catch { return false; }
  },
  async list(): Promise<Entry[]> {
    const all = await run<Entry[]>('entries', 'readonly', (s) => s.getAll());
    return (all ?? []).sort((a, b) => b.date.localeCompare(a.date) || b.createdAt.localeCompare(a.createdAt));
  },
  get(id: string): Promise<Entry | undefined> {
    return run<Entry | undefined>('entries', 'readonly', (s) => s.get(id));
  },
  put(e: Entry): Promise<void> {
    return run<IDBValidKey>('entries', 'readwrite', (s) => s.put(e)).then(() => undefined);
  },
  remove(id: string): Promise<void> {
    return run<undefined>('entries', 'readwrite', (s) => s.delete(id)).then(() => undefined);
  },
  async findByDay(dateIso: string, exceptId?: string): Promise<Entry | undefined> {
    const all = await libraryDb.list();
    return all.find((e) => e.id !== exceptId && sameDay(e.date, dateIso));
  },
  // Drafts are keyed per target: draftKey(null) = 'new', draftKey(id) = 'edit:<id>'.
  async getDraft(key: string): Promise<Draft | undefined> {
    await migrateLegacyDraft().catch((e) => console.warn('[mania] legacy draft migration failed', e));
    return run<Draft | undefined>('draft', 'readonly', (s) => s.get(key));
  },
  putDraft(key: string, d: Draft): Promise<void> {
    return run<IDBValidKey>('draft', 'readwrite', (s) => s.put(d, key)).then(() => undefined);
  },
  clearDraft(key: string): Promise<void> {
    return run<undefined>('draft', 'readwrite', (s) => s.delete(key)).then(() => undefined);
  },
  // One-time import of the old localStorage journals. The legacy key is left untouched
  // as a backup; a flag stops re-import. Malformed rows are skipped, never thrown.
  async migrateFromLocalStorage(): Promise<number> {
    let raw: string | null = null;
    try {
      if (localStorage.getItem(MIGRATED_FLAG)) return 0;
      raw = localStorage.getItem(LEGACY_KEY);
    } catch { return 0; }
    let rows: unknown[] = [];
    try { const parsed = raw ? JSON.parse(raw) : []; rows = Array.isArray(parsed) ? parsed : []; }
    catch (err) { console.warn('[mania] legacy journals unreadable, skipping migration', err); }
    let n = 0;
    for (const row of rows.slice(0, 5000)) {
      const e = legacyToEntry(row, newSeed());
      if (!e) continue;
      if (await libraryDb.get(e.id)) continue;
      await libraryDb.put(e);
      n++;
    }
    try { localStorage.setItem(MIGRATED_FLAG, new Date().toISOString()); } catch { /* best effort */ }
    return n;
  },
};
